import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import DiscordIcon from './DiscordIcon';
import StarCanvas from './StarCanvas';

const LANGS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

const LoginIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

export default function PageHeader({ lang, setLang, t, currentSlug }) {
  const { user, isAuthenticated, isAdmin, isOwner, isMember, loginWithDiscord, logout, loading: authLoading, userProfile } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const menuRef = useRef(null);
  const langRef = useRef(null);

  const { data: clansData } = useQuery({
    queryKey: ['lp_clans'],
    queryFn: () => apiGet('/v1/clans'),
    staleTime: 300_000,
  });
  const clans = Array.isArray(clansData) ? clansData : [];
  // Use currentSlug if provided (from URL context), otherwise fall back to first membership
  const effectiveSlug = currentSlug || userProfile?.memberships?.[0]?.slug || clans[0]?.slug || null;

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogin = async () => { try { await loginWithDiscord(); } catch {} };
  const handleLogout = async () => { try { await logout(); setMenuOpen(false); } catch {} };

  const userName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || '';
  const userAvatar = user?.user_metadata?.avatar_url;

  return (
    <nav className="lp-nav" style={{ background: 'rgba(15,23,42,0.92)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(71,85,105,0.3)', position: 'sticky', top: 0, zIndex: 50, paddingBottom: '0', marginBottom: 0 }}>
      <style>{`@media (min-width: 769px) { .page-header-logo { margin-top: -3rem; margin-bottom: -3rem; } }`}</style>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', background: 'radial-gradient(ellipse at 60% 20%, rgba(251,191,36,0.06) 0%, transparent 55%), radial-gradient(ellipse at 20% 80%, rgba(59,130,246,0.07) 0%, transparent 50%)' }}>
        <StarCanvas />
      </div>
      <div className="lp-nav-logo page-header-logo" style={{ position: 'relative', zIndex: 1 }}>
        <Link to="/">
          <img
            src="/static/img/branding/Clansight.png"
            alt="Clansight"
            className="lp-nav-logo-img"
            style={{ height: '192px', width: 'auto' }}
          />
        </Link>
      </div>
      <div className="lp-nav-right" style={{ position: 'relative', zIndex: 1 }}>
        <div className="lp-lang-switcher" ref={langRef}>
          <button onClick={() => setLangOpen(o => !o)} className="lp-lang-btn">
            <span className="lp-lang-label">{LANGS.find(l => l.code === lang)?.label || (lang || 'EN').toUpperCase()}</span>
            <span className="lp-lang-caret">{langOpen ? '▲' : '▼'}</span>
          </button>
          {langOpen && (
            <div className="lp-lang-menu">
              {LANGS.map(l => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setLangOpen(false); }}
                  className={`lp-lang-option${l.code === lang ? ' active' : ''}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {authLoading ? null : !isAuthenticated ? (
          <button onClick={handleLogin} className="lp-nav-discord" style={{ cursor: 'pointer', background: 'rgba(88,101,242,0.15)', border: '1px solid rgba(88,101,242,0.45)' }}>
            <LoginIcon />
            <span>{t ? t('lp_login') : 'Login'}</span>
          </button>
        ) : (
          <div className="lp-nav-user" ref={menuRef}>
            <button onClick={() => setMenuOpen(o => !o)} className="lp-nav-user-btn">
              {userAvatar
                ? <img src={userAvatar} alt={userName} className="lp-nav-avatar" />
                : <span className="lp-nav-avatar-fallback">👤</span>
              }
              <span className="lp-nav-username">{userName}</span>
              <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', marginLeft: '0.1rem' }}>{menuOpen ? '▲' : '▼'}</span>
            </button>
            {menuOpen && (
              <div className="lp-nav-user-menu">
                <div className="lp-nav-user-menu-header">
                  {userAvatar
                    ? <img src={userAvatar} alt={userName} className="lp-nav-avatar-lg" />
                    : <span className="lp-nav-avatar-fallback-lg">👤</span>
                  }
                  <div>
                    <div className="lp-nav-user-name">{userName}</div>
                    <div className="lp-nav-user-role">
                      {userProfile?.status === 'approved'
                        ? (userProfile?.memberships?.length > 0
                            ? userProfile.memberships.map(m => m.clan_name).join(', ')
                            : '✅ ' + (t ? t('role_approved') : 'Approved'))
                        : userProfile?.status === 'pending' ? '⏳ ' + (t ? t('role_pending') : 'Pending')
                        : '❌ ' + (t ? t('lp_not_registered') : 'Not registered')}
                    </div>
                  </div>
                </div>
                {isMember && effectiveSlug && (
                  <Link
                    to={`/clan/${effectiveSlug}`}
                    onClick={() => setMenuOpen(false)}
                    className="lp-nav-settings-btn"
                  >
                    <span>📊</span>
                    <span>{t ? t('lp_open_dashboard') : 'Dashboard'}</span>
                  </Link>
                )}
                {isOwner && (
                  <Link
                    to="/manage"
                    onClick={() => setMenuOpen(false)}
                    className="lp-nav-settings-btn"
                  >
                    <span>🏰</span>
                    <span>{t ? t('nav_manage_clans') : 'Manage Clans'}</span>
                  </Link>
                )}
                {isAdmin && effectiveSlug && (
                  <Link
                    to={`/settings/${effectiveSlug}`}
                    onClick={() => setMenuOpen(false)}
                    className="lp-nav-settings-btn"
                  >
                    <span>⚙️</span>
                    <span>{t ? t('nav_settings') : 'Settings'}</span>
                  </Link>
                )}
                <button onClick={handleLogout} className="lp-nav-logout-btn">{t ? t('lp_sign_out') : 'Sign out'}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
